import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom";
import PropTypes from "prop-types";
import format from "date-fns/format";

// Material
import { makeStyles } from "@material-ui/core/styles";
import ButtonBase from "@material-ui/core/ButtonBase";
import Card from "@material-ui/core/Card";
import CardActionArea from "@material-ui/core/CardActionArea";
import CardContent from "@material-ui/core/CardContent";
import CardMedia from "@material-ui/core/CardMedia";
import Chip from "@material-ui/core/Chip";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";

// Config
import { getCaseSubmissionUrl } from "../../config/apiConfig";
import { snackbarTypes } from "../../config/snackbar";
import colors from "../../config/colors";
import styles from "../../config/styles";

// Hooks
import useTranslate from "../../hooks/useTranslate";

// Actions
import { getCaseDetail } from "../../state/caseDetail/actions";
import { parseFetchOptions } from "../../state/user/actions";
import { toggleSnack } from "../../state/snackbar/actions";

// Utils
import { useAppInsights } from "../../utils/telemetry/AppInsights";
import isCaseSubmittedTo from "../../utils/isCaseSubmittedTo";

// Components
import EditReferenceNumberDialog from "../../components/EditReferenceNumberDialog";
import Link from "../../components/Link";
import SubmitCaseButton from "../../components/SubmitCaseButton";
import Text from "../../components/Text";

const useStyles = makeStyles(theme => ({
  root: {
    marginBottom: theme.spacing(2),
    borderRadius: "12px",
    overflow: "hidden",
    marginRight: "auto",
    width: "100%",
    maxWidth: 500,
    boxShadow: styles.values.boxShadowMedium
  },
  actionArea: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "stretch",
    "&:has($referenceLink:hover) .MuiCardActionArea-focusHighlight": {
      opacity: "0 !important"
    }
  },
  cardMedia: {
    backgroundColor: ({ color, isSubmitted, isActive }) =>
      isSubmitted || !isActive ? "#E0E0E0" : color,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minWidth: "90px",
    minHeight: "98px"
  },
  logoImg: {
    maxHeight: "47px"
  },
  cardContent: {
    display: "flex",
    alignItems: "flex-start",
    flexDirection: "column",
    justifyContent: "center"
  },
  statusBadge: {
    backgroundColor: colors.emergencyHeader,
    color: "white",
    borderRadius: theme.shape.borderRadius,
    textTransform: "uppercase",
    fontSize: "8px",
    height: "16px",
    marginBottom: theme.spacing(1)
  },
  inactiveBadge: {
    backgroundColor: colors.doveGray,
    color: "white",
    borderRadius: theme.shape.borderRadius,
    textTransform: "uppercase",
    fontSize: "8px",
    height: "16px",
    marginBottom: theme.spacing(1)
  },
  title: {
    marginBottom: theme.spacing(1),
    color: ({ isSubmitted }) => isSubmitted && colors.doveGray
  },
  referenceLink: {
    display: "inline",
    verticalAlign: "baseline",
    color: colors.pictonBlue,
    fontFamily: ["Open Sans", "sans-serif"].join(","),
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: "transparent"
    }
  }
}));

// A case can have a recipient/reference number assigned ahead of time, before
// it's actually been shared with them - so referenceNumber can be present
// even when isSubmitted is false, and takes priority over the description
// whenever it is. "Pending review" is still only for a real submission
// that hasn't gotten a reference number back yet.
const getSecondaryContent = (item, isSubmitted) => {
  if (item.submission?.referenceNumber) {
    return (
      <Text
        type="body"
        text="downloadCasePage.referenceNumberValue"
        values={{ 1: item.submission.referenceNumber }}
      />
    );
  }
  if (isSubmitted) {
    return <Text type="body" text="downloadCasePage.referencePendingReview" />;
  }
  return <Text type="body">{item.description}</Text>;
};

const ShareWithOptionItem = ({ item, to, isOwnedByUser, onReferenceSaved }) => {
  const isSubmitted = isCaseSubmittedTo(item);
  const isActive = !!item.isActive;
  const classes = useStyles({ color: item.colorHex, isSubmitted, isActive });
  const getText = useTranslate();
  const dispatch = useDispatch();
  const { caseId } = useParams();
  const { trackException } = useAppInsights();
  const [editingReference, setEditingReference] = useState(false);
  const [isSavingReference, setSavingReference] = useState(false);
  const [referenceNumberOverride, setReferenceNumberOverride] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);

  const displayItem = referenceNumberOverride
    ? {
        ...item,
        submission: {
          ...item.submission,
          referenceNumber: referenceNumberOverride
        }
      }
    : item;

  // Requires `to` - the other render path wraps content in a real <button>
  // (SubmitCaseButton), which can't contain another button.
  const canShowReferenceMenu =
    !!to && !!displayItem.submission?.referenceNumber;

  const canEditReference =
    canShowReferenceMenu &&
    isOwnedByUser &&
    !!displayItem.submission?.id &&
    !displayItem.submission?.submissionDateTime &&
    !displayItem.submission?.acceptanceDateTime;

  const handleReferenceClick = event => {
    // Stop the click from reaching the surrounding CardActionArea, which
    // would otherwise navigate away instead of opening this menu.
    event.preventDefault();
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  // CardActionArea's ripple starts on mousedown/touchstart, not click - by
  // the time handleReferenceClick's stopPropagation runs, it's too late.
  const stopMouseEvent = event => event.stopPropagation();

  const handleMenuClose = () => setMenuAnchorEl(null);

  const handleMenuItemClick = onClick => () => {
    setMenuAnchorEl(null);
    onClick();
  };

  const handleCopyReference = () => {
    navigator.clipboard.writeText(displayItem.submission.referenceNumber);
    dispatch(
      toggleSnack({
        type: snackbarTypes.SUCCESS,
        text: "common.prompts.copiedToClipboard"
      })
    );
  };

  const handleSaveReference = newValue => {
    setSavingReference(true);
    dispatch(
      parseFetchOptions(async ({ headers }) => {
        const response = await fetch(
          getCaseSubmissionUrl(caseId, item.submission.id),
          {
            method: "PATCH",
            headers: { ...headers, "Content-Type": "application/json" },
            body: JSON.stringify({ referenceNumber: newValue })
          }
        ).catch(() => null);

        setSavingReference(false);

        if (response && response.ok) {
          setReferenceNumberOverride(newValue);
          setEditingReference(false);
          // This card's own local state shows the new value immediately, but
          // state.caseDetail.data.submissions (read elsewhere, e.g. the
          // "Crime Previously Reported?" field) is otherwise only ever
          // refreshed by a full case-detail fetch.
          dispatch(getCaseDetail({ caseId, isUpdate: true }));
          // Some parents (e.g. CaseSharingCard's suggested-recipient card)
          // fetch this same recipient's data independently and won't see
          // either of the above - let them patch their own copy too.
          onReferenceSaved(newValue);
        } else {
          dispatch(toggleSnack({ type: snackbarTypes.ERROR }));
          trackException(
            `Failed to update reference number for case ${caseId}`
          );
        }
      })
    );
  };

  const content = (
    <>
      <CardMedia className={classes.cardMedia}>
        <img className={classes.logoImg} src={item.logoUri} alt="" />
      </CardMedia>
      <CardContent className={classes.cardContent}>
        {isSubmitted && item.submission.submissionDateTime && (
          <Chip
            component="span"
            className={classes.statusBadge}
            label={getText("common.valueLabels.sharedOnDate", {
              1: format(
                new Date(item.submission.submissionDateTime),
                getText("common.dateFormat.fullDate")
              )
            })}
          />
        )}
        {!isActive && (
          <Chip
            component="span"
            className={classes.inactiveBadge}
            label={getText("common.valueLabels.notCurrentlyConnected")}
          />
        )}
        <Text className={classes.title} type="subtitle1Big">
          {item.name}
        </Text>
        {canShowReferenceMenu ? (
          <Text
            type="body"
            text="downloadCasePage.referenceNumberValue"
            values={{
              isComponent: true,
              1: (
                <ButtonBase
                  disableRipple
                  disableTouchRipple
                  className={classes.referenceLink}
                  onClick={handleReferenceClick}
                  onMouseDown={stopMouseEvent}
                  onTouchStart={stopMouseEvent}
                >
                  {displayItem.submission.referenceNumber}
                </ButtonBase>
              )
            }}
          />
        ) : (
          getSecondaryContent(displayItem, isSubmitted)
        )}
      </CardContent>
    </>
  );

  return (
    <Card className={classes.root} component="li">
      {to ? (
        <CardActionArea component={Link} to={to} className={classes.actionArea}>
          {content}
        </CardActionArea>
      ) : (
        <SubmitCaseButton
          disabled={isSubmitted || !isActive}
          className={classes.actionArea}
          recipient={item}
        >
          {content}
        </SubmitCaseButton>
      )}
      {canShowReferenceMenu && (
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={handleMenuItemClick(handleCopyReference)}>
            {getText("common.prompts.copyReferenceNumber")}
          </MenuItem>
          {canEditReference && (
            <MenuItem
              onClick={handleMenuItemClick(() => setEditingReference(true))}
            >
              {getText("common.prompts.editReferenceNumber")}
            </MenuItem>
          )}
        </Menu>
      )}
      {editingReference && (
        <EditReferenceNumberDialog
          initialValue={displayItem.submission.referenceNumber}
          isSaving={isSavingReference}
          onClose={() => setEditingReference(false)}
          onSave={handleSaveReference}
        />
      )}
    </Card>
  );
};

ShareWithOptionItem.propTypes = {
  item: PropTypes.shape({
    name: PropTypes.string,
    description: PropTypes.string,
    logoUri: PropTypes.string,
    colorHex: PropTypes.string,
    submission: PropTypes.shape({
      id: PropTypes.string,
      submissionDateTime: PropTypes.string,
      acceptanceDateTime: PropTypes.string,
      status: PropTypes.string,
      referenceNumber: PropTypes.string
    }),
    isActive: PropTypes.bool
  }).isRequired,
  // When set, renders as a plain link to that URL instead of the
  // SubmitCaseButton confirm-then-submit flow - used by CaseSharingCard,
  // which just wants to send the user to the Share With page, not start a
  // new submission from its summary card.
  to: PropTypes.string,
  // Only the case owner can edit a recipient's reference number (copying it
  // is open to anyone) - defaults to false so existing call sites (which
  // never offer editing) are unaffected.
  isOwnedByUser: PropTypes.bool,
  // Called with the new reference number after a successful save, for
  // parents that keep their own separate copy of this recipient's data.
  onReferenceSaved: PropTypes.func
};

ShareWithOptionItem.defaultProps = {
  to: null,
  isOwnedByUser: false,
  onReferenceSaved: () => {}
};

export default ShareWithOptionItem;
